import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export type HealthStatus = "healthy" | "warning" | "error" | null;

export interface AccountHealthRow {
  accountId: string;
  platform: string;
  username: string | null;
  status: HealthStatus;
  tokenExpiresAt: string | null;
  canPost: boolean;
  canFetchAnalytics: boolean;
  missingScopes: string[];
  recommendation: string | null;
  checkedAt: string | null;
}

export interface AccountHealthSummary {
  total: number;
  healthy: number;
  warning: number;
  error: number;
  needsReconnect: number;
}

export interface AccountHealthResponse {
  summary: AccountHealthSummary;
  accounts: AccountHealthRow[];
}

export function useAccountHealth(brandId: string) {
  return useQuery({
    queryKey: ["account-health", brandId],
    queryFn: () => apiFetch<AccountHealthResponse>(`/brands/${brandId}/accounts/health`),
    enabled: !!brandId,
    staleTime: 60_000,
  });
}

export function useRefreshAccountHealth(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ jobId: string }>(`/brands/${brandId}/accounts/refresh-health`, {
        method: "POST",
      }),
    onSuccess: () => {
      // Invalidate after a short delay so the worker has time to run.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["account-health", brandId] });
      }, 1500);
    },
  });
}
